import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase, TABLES } from '../config/supabase'
import toast from 'react-hot-toast'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [family, setFamily] = useState(null)
  const [needsOnboarding, setNeedsOnboarding] = useState(false)
  const [onboardingCompleted, setOnboardingCompleted] = useState(false)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session:', session?.user?.id)
      setUser(session?.user ?? null)
      if (session?.user) {
        checkUserSetup(session.user.id)
      }
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session?.user?.id)
        setUser(session?.user ?? null)
        
        if (session?.user) {
          // Only check user setup if not coming from onboarding completion
          if (event === 'SIGNED_IN' && !onboardingCompleted) {
            setTimeout(() => {
              checkUserSetup(session.user.id)
            }, 500)
          }
        } else {
          setFamily(null)
          setNeedsOnboarding(false)
          setOnboardingCompleted(false)
        }
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [onboardingCompleted])

  const checkUserSetup = async (userId) => {
    try {
      console.log('🔍 Checking user setup for:', userId)
      
      // Check if user has a family
      const { data: familyData, error: familyError } = await supabase
        .from(TABLES.FAMILIES)
        .select(`
          *,
          children:${TABLES.CHILDREN} (*)
        `)
        .eq('parent_id', userId)
        .maybeSingle()

      if (familyError && familyError.code !== 'PGRST116') {
        console.error('Error checking family:', familyError)
        return
      }

      if (familyData) {
        console.log('✅ Family found:', familyData.name, 'ID:', familyData.id)
        setFamily(familyData)
        setNeedsOnboarding(false)
        console.log('✅ User setup complete - no onboarding needed')
      } else {
        console.log('❌ No family found, user needs onboarding')
        setFamily(null)
        setNeedsOnboarding(true)
      }
    } catch (error) {
      console.error('Unexpected error checking user setup:', error)
    }
  }

  const loadFamily = async (userId) => {
    try {
      console.log('Loading family for user:', userId)
      const { data: familyData, error: familyError } = await supabase
        .from(TABLES.FAMILIES)
        .select(`
          *,
          children:${TABLES.CHILDREN} (*)
        `)
        .eq('parent_id', userId)
        .maybeSingle()

      if (familyError) {
        console.error('Error loading family:', familyError)
        if (familyError.code !== 'PGRST116') {
          toast.error('Error loading family data')
        }
        return
      }

      console.log('Family loaded:', familyData)
      setFamily(familyData)
      setNeedsOnboarding(!familyData)
    } catch (error) {
      console.error('Unexpected error loading family:', error)
    }
  }

  const completeOnboarding = async (familyData) => {
    try {
      console.log('🎯 Completing onboarding with family data:', familyData)
      
      // Set the completed flag FIRST to prevent re-checking
      setOnboardingCompleted(true)
      setNeedsOnboarding(false)
      
      // Update the family state
      setFamily(familyData)
      
      console.log('🎉 Onboarding completed successfully!')
      console.log('🚀 State updated: needsOnboarding=false, family=', familyData.name)
      return true
    } catch (error) {
      console.error('Error completing onboarding:', error)
      return false
    }
  }

  const addChild = async (childData) => {
    try {
      if (!family) {
        toast.error('No family found. Please complete onboarding first.')
        return false
      }

      console.log('Adding child:', childData)
      const { data, error } = await supabase
        .from(TABLES.CHILDREN)
        .insert([{
          ...childData,
          family_id: family.id,
          created_at: new Date().toISOString()
        }])
        .select()
        .single()

      if (error) {
        console.error('Error adding child:', error)
        throw error
      }

      // Update family state with new child
      setFamily(prev => ({
        ...prev,
        children: [...(prev.children || []), data]
      }))

      toast.success(`${childData.name} has been added to your family! 🎉`)
      return true
    } catch (error) {
      console.error('Error adding child:', error)
      toast.error('Error adding child. Please try again.')
      return false
    }
  }

  const updateChild = async (childId, childData) => {
    try {
      if (!family) {
        toast.error('No family found.')
        return false
      }

      console.log('Updating child:', childId, childData)
      const { data, error } = await supabase
        .from(TABLES.CHILDREN)
        .update({
          ...childData,
          updated_at: new Date().toISOString()
        })
        .eq('id', childId)
        .eq('family_id', family.id)
        .select()
        .single()

      if (error) {
        console.error('Error updating child:', error)
        throw error
      }

      // Update family state with updated child
      setFamily(prev => ({
        ...prev,
        children: prev.children.map(child => 
          child.id === childId ? data : child
        )
      }))

      return true
    } catch (error) {
      console.error('Error updating child:', error)
      toast.error('Error updating child. Please try again.')
      return false
    }
  }

  const removeChild = async (childId) => {
    try {
      if (!family) {
        toast.error('No family found.')
        return false
      }

      console.log('Removing child:', childId)
      const { error } = await supabase
        .from(TABLES.CHILDREN)
        .delete()
        .eq('id', childId)
        .eq('family_id', family.id)

      if (error) {
        console.error('Error removing child:', error)
        throw error
      }

      // Update family state by removing the child
      setFamily(prev => ({
        ...prev,
        children: prev.children.filter(child => child.id !== childId)
      }))

      return true
    } catch (error) {
      console.error('Error removing child:', error)
      toast.error('Error removing child. Please try again.')
      return false
    }
  }

  const signUp = async (email, password) => {
    try {
      console.log('Starting signup process for:', email)
      
      // Sign up the user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
      })

      if (authError) {
        console.error('Auth signup error:', authError)
        throw authError
      }

      if (authData.user) {
        console.log('User created successfully:', authData.user.id)
        toast.success('Account created successfully! Please check your email to verify your account.')
        return { data: authData, error: null }
      }
    } catch (error) {
      console.error('Signup error:', error)
      let errorMessage = 'Failed to create account. Please try again.'
      
      if (error.message?.includes('User already registered')) {
        errorMessage = 'An account with this email already exists. Please sign in instead.'
      } else if (error.message?.includes('Invalid email')) {
        errorMessage = 'Please enter a valid email address.'
      } else if (error.message?.includes('Password')) {
        errorMessage = error.message
      }
      
      toast.error(errorMessage)
      return { data: null, error }
    }
  }

  const signIn = async (email, password) => {
    try {
      console.log('Signing in user:', email)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      console.log('Sign in successful for user:', data.user?.id)
      toast.success('Welcome back!')
      return { data, error: null }
    } catch (error) {
      console.error('Signin error:', error)
      toast.error(error.message || 'Failed to sign in. Please check your credentials.')
      return { data: null, error }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      
      setFamily(null)
      setNeedsOnboarding(false)
      setOnboardingCompleted(false)
      toast.success('Signed out successfully!')
    } catch (error) {
      console.error('Signout error:', error)
      toast.error(error.message || 'Failed to sign out.')
    }
  }

  const value = {
    user,
    family,
    loading,
    needsOnboarding,
    signUp,
    signIn,
    signOut,
    loadFamily,
    addChild,
    updateChild,
    removeChild,
    setFamily,
    setNeedsOnboarding,
    completeOnboarding
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}